import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { runDecomModels } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { DecomModel } from '../../../../../types';
import Typography from '@mui/material/Typography';

interface RunDecomPoliciesDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  models: DecomModel[];
}

export default function RunDecomPoliciesDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  models,
}: RunDecomPoliciesDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const selectedModels =
    models.filter((item) => selectedRows.includes(item.id)) || [];

  const { mutate, isPending } = useMutation({
    mutationFn: () => runDecomModels(selectedRows, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          selectedRows.length === 1
            ? 'The run decom policy task has begun for the selected policy.'
            : 'The selected decom policies run tasks have begun.',
      });

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue running the selected decom policy. Please refresh and try again.'
            : 'There was an issue running the selected decom policies. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.automation.decommissioning.run_decom_policies, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        ToolsRoles.RunDecommissioningProcess.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Run Decommissioning ${
        selectedRows.length === 1 ? 'Policy' : 'Policies'
      }`}
      dialogContent={
        <>
          {selectedRows.length > 1 && (
            <Typography>
              Run{' '}
              <strong>{selectedRows.length} decommissioning policies</strong>.
            </Typography>
          )}
          {selectedRows.length === 1 && (
            <Typography>
              Run decommissioning policy{' '}
              <strong>{selectedModels[0].name}</strong>.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: `Run ${selectedRows.length === 1 ? 'Policy' : 'Policies'}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
